import { Paper } from "@mui/material";
import isHotkey from "is-hotkey";
import { useCallback, useEffect, useMemo } from "react";
import { createEditor, Editor, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Editable, Slate, withReact } from "slate-react";
import { BlockButton, LinkButton, MarkButton, toggleMark, Toolbar } from "../utils/richFormatterButtons.js";
import { Element, Leaf, normalizeSlateValue, withLinks } from "../utils/richFormatterUtils.js";

export const RichtextFormatter = ({
    disabled, initialValue, comment, setComment, style,
    setTextfieldHelpText, setErrorTextfield, label
}) => {
    const hotkeys = {
        "mod+b": "bold",
        "mod+i": "italic",
        "mod+u": "underline",
    };
    const editor = useMemo(
        () => withLinks(withHistory(withReact(createEditor())), disabled),
        [disabled]
    );

    // Update the disabled state dynamically
    useEffect(() => {
        editor.disabled = disabled;
    }, [editor, disabled]);

    const renderElement = useCallback((elementProps) => <Element {...elementProps} editor={editor} />, [editor]);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

    const editorValue = useMemo(() => {
        let value = comment || initialValue;
        if (typeof value === "string" && value.trim()) {
            try {
                value = JSON.parse(value);
            } catch (e) {
                console.error("Error parsing initial value:", e);
                value = null;
            }
        }

        return normalizeSlateValue(value);
    }, [comment, initialValue]);

    useEffect(() => {
        let value = comment || initialValue;

        if (typeof value === 'string' && value.trim()) {
            try {
                value = JSON.parse(value);
            } catch (e) {
                console.error('Error parsing value in effect:', e);
                value = null;
            }
        }
        const normalized = normalizeSlateValue(value);

        const currentValue = JSON.stringify(editor.children);
        const newValue = JSON.stringify(normalized);

        if (currentValue !== newValue) {
            editor.selection = null;
            editor.history = { redos: [], undos: [] };
            editor.children = [];
            editor.children = normalized;
            Editor.normalize(editor, { force: true });

            try {
                const start = Editor.start(editor, []);
                Transforms.select(editor, start);
            } catch (e) {
                console.warn("Could not set selection:", e);
            }
        }
    }, [comment, initialValue, editor]);

    return (
        <Slate
            editor={editor}
            initialValue={editorValue}
            onChange={(value) => {
                const isAstChange = editor.operations.some(
                    (op) => op.type !== "set_selection"
                );

                if (isAstChange) {
                    if (setComment) {
                        setComment(value);
                    }
                    if (setTextfieldHelpText) {
                        setTextfieldHelpText("");
                    }
                    if (setErrorTextfield) {
                        setErrorTextfield(false);
                    }
                }
            }}
        >
            <style>
                {`
                    [data-slate-editor] [data-slate-placeholder] {
                        text-align: left !important;
                        padding-left: 0 !important;
                        margin-left: 0 !important;
                    }
                `}
            </style>
            <Paper variant="outlined" style={{ ...(style ? style : {}) }}>
                <Toolbar>
                    <MarkButton format="bold" icon="format_bold" />
                    <MarkButton format="italic" icon="format_italic" />
                    <MarkButton format="underline" icon="format_underlined" />
                    <BlockButton format="heading-one" icon="looks_one" />
                    <BlockButton format="heading-two" icon="looks_two" />
                    <BlockButton format="numbered-list" icon="format_list_numbered" />
                    <BlockButton format="bulleted-list" icon="format_list_bulleted" />
                    <BlockButton format="left" icon="format_align_left" />
                    <BlockButton format="center" icon="format_align_center" />
                    <BlockButton format="right" icon="format_align_right" />
                    <BlockButton format="justify" icon="format_align_justify" />
                    <LinkButton />
                </Toolbar>
                <Editable
                    readOnly={disabled || false}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    placeholder={disabled ? '' : label}
                    spellCheck
                    autoFocus
                    style={{
                        position: "relative",
                        minHeight: "100px",
                        outline: "none",
                        padding: "0px 8px 8px 8px",
                        color: disabled ? "rgba(0, 0, 0, 0.38)" : "inherit",
                    }}
                    onKeyDown={(event) => {
                        for (const hotkey in hotkeys) {
                            if (isHotkey(hotkey, event)) {
                                event.preventDefault();
                                const mark = hotkeys[hotkey];
                                toggleMark(editor, mark);
                            }
                        }
                    }}
                />
            </Paper>
        </Slate>
    );
};

export default RichtextFormatter;
